import React from 'react';
import {
  AgnesPopover,
  AgnesPopoverTrigger,
  AgnesPopoverContent,
  AgnesButton,
  AgnesInput,
  AgnesSelect,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesSelectContent,
  AgnesSelectItem,
} from '@agnes/ui';
import { Filter, Trash2, X, Calendar } from 'lucide-react';
import { labelFiltro } from '@/lib/filtros';
import {
  type FiltrosIncidenciaNatureza,
  type OpcoesFiltro,
  type CampoSelect,
} from '@/stores/useIncidenciaNaturezaStore';

const TRIGGER_CLS =
  '[&>[data-slot=select-value]]:min-w-0 [&>[data-slot=select-value]]:flex-1 [&>[data-slot=select-value]]:truncate [&>[data-slot=select-value]]:text-left';

const VAZIO: FiltrosIncidenciaNatureza = {
  situacao: '',
  tipo: '',
  cp: '',
  irrf: '',
  fgts: '',
  cprp: '',
  pisPasep: '',
  teto: '',
  alteradoPor: '',
  atualizacao: '',
};

// código - descrição (contrato da versão antiga). Ordem da homolog.
const SELECTS_CODIGO: {
  campo: Extract<CampoSelect, 'tipo' | 'cp' | 'irrf' | 'fgts' | 'cprp' | 'pisPasep'>;
  label: string;
}[] = [
  { campo: 'tipo', label: 'Tipo' },
  { campo: 'cp', label: 'CP' },
  { campo: 'irrf', label: 'IRRF' },
  { campo: 'fgts', label: 'FGTS' },
  { campo: 'cprp', label: 'CPRP' },
  { campo: 'pisPasep', label: 'PIS/PASEP' },
];

const paraBR = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '');

type Props = {
  opcoes: OpcoesFiltro;
  onApply: (f: FiltrosIncidenciaNatureza) => void;
  count?: number;
};

export default function IncidenciaNaturezaFilter({ opcoes, onApply, count = 0 }: Props) {
  const [open, setOpen] = React.useState(false);
  const [f, setF] = React.useState<FiltrosIncidenciaNatureza>(VAZIO);
  const [atualizacaoIso, setAtualizacaoIso] = React.useState('');
  const set = (k: CampoSelect) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  const aplicar = () => {
    onApply({ ...f, atualizacao: paraBR(atualizacaoIso) });
    setOpen(false);
  };
  const limpar = () => {
    setF(VAZIO);
    setAtualizacaoIso('');
    onApply(VAZIO);
  };

  const strSelect = (campo: CampoSelect, label: string, options: string[]) => (
    <AgnesSelect value={f[campo]} onValueChange={set(campo)}>
      <AgnesSelectTrigger className={TRIGGER_CLS} data-qa={`incidencia-natureza-filtro-${campo}`}>
        <AgnesSelectValue placeholder={label} />
      </AgnesSelectTrigger>
      <AgnesSelectContent>
        {options.map((o) => (
          <AgnesSelectItem key={o} value={o}>
            {o}
          </AgnesSelectItem>
        ))}
      </AgnesSelectContent>
    </AgnesSelect>
  );

  return (
    <AgnesPopover open={open} onOpenChange={setOpen}>
      <AgnesPopoverTrigger asChild>
        <AgnesButton
          variant="outlined"
          startIcon={<Filter className="h-4 w-4" />}
          data-qa="incidencia-natureza-filtro"
          aria-label={count > 0 ? `Filtro, ${count} ativos` : 'Filtro'}
        >
          {labelFiltro('Filtro', count)}
        </AgnesButton>
      </AgnesPopoverTrigger>

      <AgnesPopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-agnes-border px-4 py-3">
          <span className="text-base font-semibold text-agnes-text">Filtro</span>
          <button
            type="button"
            aria-label="Fechar"
            onClick={() => setOpen(false)}
            className="text-agnes-text-muted hover:text-agnes-text"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto px-4 py-4">
          {strSelect('situacao', 'Situação', opcoes.situacao)}

          {SELECTS_CODIGO.map(({ campo, label }) => (
            <AgnesSelect key={campo} value={f[campo]} onValueChange={set(campo)}>
              <AgnesSelectTrigger
                className={TRIGGER_CLS}
                data-qa={`incidencia-natureza-filtro-${campo}`}
              >
                <AgnesSelectValue placeholder={label} />
              </AgnesSelectTrigger>
              <AgnesSelectContent>
                {opcoes[campo].map((o) => (
                  <AgnesSelectItem key={o.value} value={o.value}>
                    {o.value} - {o.title}
                  </AgnesSelectItem>
                ))}
              </AgnesSelectContent>
            </AgnesSelect>
          ))}

          {strSelect('teto', 'Teto Remuneratório', opcoes.teto)}

          <AgnesInput
            type="date"
            value={atualizacaoIso}
            onChange={(e) => setAtualizacaoIso(e.target.value)}
            placeholder="Atualização"
            endIcon={<Calendar className="h-4 w-4" />}
            data-qa="incidencia-natureza-filtro-atualizacao"
          />

          {strSelect('alteradoPor', 'Atualizado por', opcoes.alteradoPor)}
        </div>

        <div className="flex flex-col gap-2 border-t border-agnes-border px-4 py-3">
          <AgnesButton
            variant="primary"
            onClick={aplicar}
            className="w-full"
            data-qa="incidencia-natureza-filtro-aplicar"
          >
            Aplicar filtro
          </AgnesButton>
          <AgnesButton
            variant="outlined"
            onClick={limpar}
            startIcon={<Trash2 className="h-4 w-4" />}
            className="w-full"
            data-qa="incidencia-natureza-filtro-limpar"
          >
            Limpar filtro
          </AgnesButton>
        </div>
      </AgnesPopoverContent>
    </AgnesPopover>
  );
}
